import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Event } from "@/types";
import { Logo, IconButton } from "@components/atoms";
import { SearchPanel } from "@components/molecules";
import { UserMenu } from "@components/organisms/UserMenu";
import { LoginModal } from "@components/organisms/LoginModal";
import { useAuth } from "@app/providers/useAuth";
import { ROUTES } from "@shared/constants";
import { Ticket, Search } from "lucide-react";
import { cn } from "@shared/utils";

interface TopNavbarProps {
  events: Event[];
  /** When true, uses opaque gradient bg instead of transparent */
  opaque?: boolean;
}

/**
 * Top navigation bar organism.
 * Transparent (or opaque gradient) background, logo left, action buttons right.
 * Search icon toggles the search panel overlay.
 * Shows Login button or UserMenu depending on auth state.
 */
export function TopNavbar({ events, opaque = false }: TopNavbarProps) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const { isAuthenticated, isLoading, user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleMyTicketsClick = () => {
    if (user) {
      navigate(ROUTES.MY_TICKETS);
    } else {
      // User not authenticated, show login modal
      setShowLoginModal(true);
    }
  };

  return (
    <>
      <nav
        className={cn(
          "relative w-full px-4 sm:px-8 lg:px-12 py-4 sm:py-6",
          opaque && "bg-gradient-to-b from-neutral-60 to-primary-110",
        )}
        style={opaque ? { height: 90 } : undefined}
      >
        <div className="flex items-center justify-between">
          <Logo
            variant="white"
            width={140}
            className="sm:w-[200px]"
            onClick={() => navigate(ROUTES.HOME)}
          />

          <div
            className={cn(
              "flex items-center gap-4 transition-opacity",
              searchOpen && "opacity-0 pointer-events-none",
            )}
          >
            <IconButton
              size={24}
              className="text-white"
              onClick={handleMyTicketsClick}
            >
              <Ticket size={24} />
            </IconButton>
            <IconButton
              size={24}
              className="text-white"
              onClick={() => setSearchOpen(true)}
            >
              <Search size={24} />
            </IconButton>

            {isLoading ? (
              <div className="w-5 h-5 rounded-full bg-transparent animate-pulse" />
            ) : isAuthenticated ? (
              <UserMenu />
            ) : (
              <button
                className="ml-1 cursor-pointer rounded-full bg-secondary-120 px-6 sm:px-[40px] py-[8px] text-primary-110 font-semibold border-none transition-colors hover:opacity-90"
                style={{ height: 40, fontSize: 14 }}
                onClick={() => setShowLoginModal(true)}
              >
                Login
              </button>
            )}
          </div>
        </div>

        <SearchPanel
          events={events}
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
        />
      </nav>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
}
